/**
 * <DeviceCheckSoundTest> — l'écoute des haut-parleurs, au test du matériel (molecule).
 *
 * Trois temps plutôt que trois boutons : le bouton rond joue la mélodie, la phrase à côté dit où
 * l'on en est, et la question (« Tu as entendu la mélodie ? ») ne se pose qu'une fois le son joué.
 * « Écouter » et « Je l'ai entendu » côte à côte se liraient comme un choix, alors que l'un
 * déclenche et l'autre répond.
 *
 * La consigne d'ouverture pèse autant que « Dis « allô » » au micro : ce qu'on attend de l'élève
 * se lit en premier, et plus gros. Une fois le son joué, la phrase devient un état et reprend sa
 * taille. Quand la lecture a été refusée (`blocked`), ce n'est ni son volume ni son casque : la
 * phrase le dit, et propose de réessayer.
 *
 * Contrôlée : l'état de la lecture et son niveau arrivent en props, le son est un effet de l'écran.
 *
 * Composes DS pieces only — `<IconButton>`, `<Text>`, `<LevelBars>`, `<Button>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { LevelBars } from "../../atoms/LevelBars"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"

/** Où en est la mélodie : pas jouée, en train de jouer, jouée, refusée par le navigateur ou l'appareil. */
export type UISoundTestState = "idle" | "playing" | "played" | "blocked"

export type DeviceCheckSoundTestProps = {
    state: UISoundTestState
    /** Le niveau de sortie pendant la lecture, de 0 à 1. */
    level?: number
    /** `click` sur un ordinateur (« Clique »), `tap` sur un téléphone (« Appuie »). Défaut `click`. */
    pointer?: "click" | "tap"
    /** Qui a bloqué le son : le navigateur par son nom (« Chrome »), sinon « Ton appareil ». */
    blockedBy?: string
    onPlay: () => void
    /** La réponse à « Tu as entendu la mélodie ? ». Absente : la question ne se pose pas encore. */
    onAnswer?: (heard: boolean) => void
    testID?: string
}

const TEXTS = {
    play: "Écouter la mélodie",
    playing: "Ça joue…",
    question: "Tu as entendu la mélodie ?",
    yes: "Oui, j'entends !",
    no: "Non, je n'entends rien",
    level: "Le son qui sort",
}

const VERB = { click: "Clique", tap: "Appuie" }
const DEVICE_FALLBACK = "Ton appareil"

/** Le bouton rond de la mélodie : la cible la plus visible de l'étape. */
const PLAY_SIZE = 52
/** En deçà, les deux réponses s'empilent sur toute la largeur. */
const SIDE_BY_SIDE_FROM = 420

function sentence(state: UISoundTestState, verb: string, blockedBy: string | undefined): string {
    if (state === "blocked")
        return `${blockedBy ?? DEVICE_FALLBACK} a bloqué le son. ${verb} encore une fois : ça marche presque toujours.`
    if (state === "playing") return TEXTS.playing
    if (state === "played") return `${verb} à nouveau pour réécouter.`
    return `${verb} sur le bouton.`
}

export function DeviceCheckSoundTest(props: DeviceCheckSoundTestProps) {
    const { state, level = 0, pointer = "click", blockedBy, onPlay, onAnswer, testID } = props
    const wide = useWidthAtLeast(SIDE_BY_SIDE_FROM)
    const opening = state === "idle"

    return (
        <View style={styles.root} onLayout={wide.onLayout} testID={testID}>
            <View style={styles.player}>
                <IconButton
                    iconName="volume-up"
                    size={PLAY_SIZE}
                    iconSize={24}
                    color="role-fill"
                    iconColor="text-on-role"
                    onPress={onPlay}
                    accessibilityLabel={TEXTS.play}
                />
                <View style={styles.texts}>
                    <Text
                        size={opening ? "text-lg" : "text-md"}
                        weight={opening ? "bold" : "semibold"}
                        color={state === "blocked" ? "warning-strong" : opening ? "text-strong" : "text-muted"}
                    >
                        {sentence(state, VERB[pointer], blockedBy)}
                    </Text>
                    <LevelBars level={state === "playing" ? level : 0} accessibilityLabel={TEXTS.level} />
                </View>
            </View>

            {onAnswer != null && (
                <View style={styles.question}>
                    <Text size="text-md" weight="bold" color="text-strong">
                        {TEXTS.question}
                    </Text>
                    <View style={wide.atLeast ? styles.answersRow : styles.answersColumn}>
                        <Button label={TEXTS.yes} onPress={() => onAnswer(true)} fullWidth={!wide.atLeast} />
                        <Button
                            label={TEXTS.no}
                            variant="outline"
                            onPress={() => onAnswer(false)}
                            fullWidth={!wide.atLeast}
                        />
                    </View>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        gap: theme.spacing["4"],
    },
    player: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    texts: {
        flex: 1,
        gap: theme.spacing["2"],
    },
    question: {
        gap: theme.spacing["2"],
    },
    answersRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    answersColumn: {
        gap: theme.spacing["2"],
    },
}))
